/* Selector de pack, botón de compra y barra fija. Sin librerías. Sin este script la página funciona con el pack preseleccionado (el enlace viene en el HTML).
   Tarjetas «precio por unidad» (data.mode 'unit'; ADR-0016/0017, L1): suma en céntimos enteros y cadenas ya formateadas por Liquid (data.money).
   Contrato (carrito y QA): data-pack-items = [{"id","quantity"}] en cada [data-pack-buy] y evento «pack:change» en document con {items, units,
   total_cents, per_unit_cents, compare_cents, saving_cents, available} (null en compare y saving con las tarjetas de siempre); enlace /cart/<v1>:<q1>,<v2>:<q2>.
   Con «Seguir al pack» (bloque «Precio», 21-10-2026) pinta también la fila del precio de arriba, #price-<sección>[data-pack-price], como la tarjeta elegida.
   22-10-2026 (noche, ADR-0021): el tachado es la suma de los precios de comparación de las variantes de la mezcla (data.variants[id].compare, solo si todas
   lo tienen) y la insignia, tachado − total (sin tachado, el descuento del pack); en pack:change, compare_cents = esa suma (o null) y saving_cents = la insignia.
   22-10-2026 (noche, ADR-0022): con data.packs[id].bps > 0 (ajuste discount_bps, puntos básicos), el descuento es el de Shopify, medido en /cart.js:
   el porcentaje de cada unidad redondeado hacia abajo, Σ (p × bps div 10000), en enteros y con la misma regla que Liquid (si no, falta la cadena y el
   botón se desactiva); discount no cuenta. Con bps a 0 (o sin él), el importe fijo discount, como antes.
   29-10-2026 (decisión del usuario): ~~la insignia, tachado − total~~ → el ahorro se cuenta sobre el precio normal, nunca sobre el tachado: la insignia y saving_cents
   = el descuento del pack (Σ p × bps div 10000, lo que descuenta Shopify; la misma cifra que la tarjeta del catálogo y el carrito). El tachado (compare_cents) no cambia. */
(() => {
  // División entera de no negativos sin coma flotante: (a − a mod b) / b es exacta
  const div = (a, b) => (a - (a % b)) / b;
  const toId = (id) => (Number.isSafeInteger(Number(id)) ? Number(id) : String(id));

  class PackSelector extends HTMLElement {
    // Pack con la 1.ª unidad first y las extra (por número) de extra: líneas, suma de precios, descuento, total, €/ud half-up, tachado y ahorro, en céntimos.
    // ref: suma de los precios de comparación (null si alguna unidad no lo tiene mayor que su precio); saving: ~~ref − total o, sin ref, el descuento~~ → el descuento del pack (ahorro sobre el precio normal, 29-10-2026). Pura.
    // Descuento: con pack.bps > 0, cada unidad p − (p × bps div 10000) (ADR-0022, como Shopify); si no, el importe fijo pack.discount, como antes.
    static calc(data, packId, first, extra) {
      const pack = data.packs[packId];
      if (!pack) return null;
      const q = pack.qty;
      const bps = pack.bps > 0 ? pack.bps : 0;
      const ids = [String(first)];
      for (let n = 2; n <= q; n += 1) ids.push(String((pack.mix && extra && extra[n]) || first));
      let full = 0;
      let cut = 0;
      let ref = 0;
      let available = true;
      for (const id of ids) {
        const v = data.variants[id];
        if (!v) return null;
        full += v.price;
        if (bps > 0) cut += div(v.price * bps, 10000);
        ref = ref !== null && v.compare > v.price ? ref + v.compare : null;
        if (!v.available) available = false;
      }
      const discount = bps > 0 ? cut : Math.min(pack.discount, full);
      const total = full - discount;
      // 29-10-2026 (decisión del usuario): en los packs (q > 1) la referencia es el precio de hoy de las unidades sueltas (full), solo si hay descuento;
      // el precio de comparación solo cuenta en la unidad suelta (q = 1). Así ahorro = referencia − total.
      const base = q > 1 ? (discount > 0 ? full : null) : ref;
      const counts = new Map();
      ids.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
      return {
        q,
        items: [...counts].map(([id, quantity]) => ({ id: toId(id), quantity })),
        full,
        ref: base,
        refUnit: base !== null ? div(2 * base + q, 2 * q) : null,
        saving: discount,
        total,
        perUnit: div(2 * total + q, 2 * q),
        available,
      };
    }

    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-pack-data]').textContent);
      this.unit = this.data.mode === 'unit';
      this.variantId = this.dataset.variantId;
      this.section = this.dataset.sectionId;
      // Variante de cada unidad extra (por número: 2, 3). Siguen a la de arriba hasta que el cliente cambia su desplegable (pinned).
      this.extra = {};
      this.pinned = {};
      this.addEventListener('change', (e) => {
        if (e.target.matches('.pack-card__input')) this.render();
        else if (e.target.matches('[data-pack-unit-select]')) {
          this.extra[e.target.dataset.unit] = e.target.value;
          this.pinned[e.target.dataset.unit] = true;
          this.render();
        }
      });
      this.cartBase = this.dataset.cartUrl;
      const onFirst = (id) => {
        this.variantId = String(id);
        Object.keys(this.extra).forEach((n) => {
          if (!this.pinned[n]) this.extra[n] = this.variantId;
        });
        this.render();
      };
      // Cambio de variante: el aviso de Dawn (PUB_SUB_EVENTS.variantChange, de product-info.js, con la variante ya resuelta), sin depender de
      // ningún formulario de la caja de compra. Hasta el 18-10-2026 se escuchaba el change del input[name=id] del formulario de cuotas del
      // bloque «Precio»: sin ese bloque, los totales no cambiaban. Sin pubsub (no debería pasar), se queda ese change como respaldo.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data || !data.variant || String(data.sectionId) !== String(this.section)) return;
          onFirst(data.variant.id);
        });
      } else {
        this.onVariant = (e) => {
          if (!e.target.matches || !e.target.matches('input[name="id"]') || !e.target.closest('product-info')) return;
          onFirst(e.target.value);
        };
        document.addEventListener('change', this.onVariant);
      }
      if (this.unit) {
        // Los desplegables como los deja el navegador (puede restaurarlos al recargar); manda la tarjeta elegida
        const checked = this.querySelector('.pack-card__input:checked');
        const own = checked ? [...checked.closest('.pack-card').querySelectorAll('[data-pack-unit-select]')] : [];
        [...own, ...this.querySelectorAll('[data-pack-unit-select]')].forEach((s) => {
          const n = s.dataset.unit;
          if (n in this.extra) return;
          this.extra[n] = s.value;
          if (s.value !== this.variantId) this.pinned[n] = true;
        });
        this.render();
      } else this.publish(this.classicState());
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      if (this.onVariant) document.removeEventListener('change', this.onVariant);
    }

    // Líneas del pack elegido en cada botón y, si cambió algo, pack:change
    publish(detail) {
      if (!detail) return;
      const items = JSON.stringify(detail.items);
      document.querySelectorAll(`[data-pack-buy="${this.section}"]`).forEach((btn) => btn.setAttribute('data-pack-items', items));
      const key = JSON.stringify(detail);
      if (key === this.published) return;
      this.published = key;
      document.dispatchEvent(new CustomEvent('pack:change', { detail }));
    }

    classicState() {
      const checked = this.querySelector('.pack-card__input:checked');
      const variant = this.data.variants[this.variantId];
      const info = checked && variant && variant.packs[checked.value];
      if (!info) return null;
      const q = Number(checked.dataset.qty);
      return {
        items: [{ id: toId(this.variantId), quantity: q }],
        units: q,
        total_cents: info.cents,
        per_unit_cents: div(2 * info.cents + q, 2 * q),
        compare_cents: null,
        saving_cents: null,
        available: !!variant.available,
      };
    }

    render() {
      if (this.unit) {
        this.renderUnit();
        return;
      }
      const variant = this.data.variants[this.variantId];
      const checked = this.querySelector('.pack-card__input:checked');
      if (!checked) return;
      const set = (el, text) => {
        el.textContent = text;
        el.hidden = !text;
      };
      this.querySelectorAll('.pack-card').forEach((card) => {
        const input = card.querySelector('.pack-card__input');
        const info = variant && variant.packs[input.value];
        if (!info) return;
        set(card.querySelector('[data-pack-total]'), info.total);
        set(card.querySelector('[data-pack-unit]'), info.unit);
        set(card.querySelector('[data-pack-save]'), info.save);
      });

      const info = variant && variant.packs[checked.value];
      const qty = checked.dataset.qty;
      const title = checked.dataset.title;
      document.querySelectorAll(`[data-pack-buy="${this.section}"]`).forEach((btn) => {
        const template = btn.dataset.template || '';
        if (!variant || !variant.available || !info) {
          btn.removeAttribute('href');
          btn.setAttribute('aria-disabled', 'true');
          btn.textContent = btn.dataset.soldOut || '';
          return;
        }
        btn.removeAttribute('aria-disabled');
        // Barra fija antes de que el cliente vea el selector: su botón baja a los packs en vez de abrir el pago (lo decide StickyBuy)
        const choosing = btn.closest('sticky-buy[data-choosing]');
        if (choosing) {
          btn.setAttribute('href', '#packs');
          btn.textContent = choosing.dataset.choose;
          return;
        }
        btn.setAttribute('href', `${this.cartBase}/${this.variantId}:${qty}`);
        btn.textContent = template.replace('{title}', title).replace('{total}', info.total).replace('{qty}', qty);
      });
      document.querySelectorAll(`sticky-buy[data-section-id="${this.section}"]`).forEach((bar) => {
        // Barra fija antes de que el cliente vea el selector: el resumen dice el texto de data-from («Desde») y el total más bajo
        // entre los packs de la variante, que es el menor de los que ya trae el selector (cents). Con el texto vacío, con la variante
        // agotada o sin ese dato, el resumen es el del pack elegido, como siempre.
        const choosing = variant && variant.available && bar.dataset.from && bar.hasAttribute('data-choosing');
        const low = choosing ? Object.values(variant.packs).reduce((a, b) => (!Number.isFinite(b.cents) || (a && a.cents <= b.cents) ? a : b), null) : null;
        const shown = low || info;
        if (shown) {
          bar.querySelector('[data-pack-summary-title]').textContent = low ? bar.dataset.from : title;
          bar.querySelector('[data-pack-summary-total]').textContent = shown.total;
        }
      });
      this.publish(this.classicState());
    }

    // Cada tarjeta con su mezcla; la cinta con guarda, solo con el menor €/ud de las que se ven. Si faltara una cadena (Liquid las calcula todas), el botón se desactiva.
    renderUnit() {
      const data = this.data;
      const checked = this.querySelector('.pack-card__input:checked');
      if (!checked) return;
      const money = (c) => data.money[c] || '';
      const fill = (text, c) => (money(c) ? text.replace('{amount}', () => money(c)) : '');
      const set = (el, text) => {
        if (!el) return;
        el.textContent = text;
        el.hidden = !text;
      };
      this.querySelectorAll('[data-pack-unit-select]').forEach((s) => {
        const id = this.extra[s.dataset.unit];
        if (id && s.value !== id) s.value = id;
      });
      const cards = [...this.querySelectorAll('.pack-card')].map((card) => {
        const input = card.querySelector('.pack-card__input');
        return { card, input, r: PackSelector.calc(data, input.value, this.variantId, this.extra) };
      });
      const shown = cards.filter((c) => c.r);
      const lowUnit = Math.min(...shown.map((c) => c.r.perUnit));
      shown.forEach(({ card, r }) => {
        set(card.querySelector('[data-pack-per-unit]'), fill(data.unitText, r.perUnit));
        set(card.querySelector('[data-pack-total]'), money(r.total));
        set(card.querySelector('[data-pack-ref]'), r.ref !== null ? money(r.ref) : '');
        set(card.querySelector('[data-pack-unit-ref]'), r.ref !== null && r.q > 1 ? money(r.refUnit) : '');
        set(card.querySelector('[data-pack-badge]'), r.saving > 0 ? fill(data.saveText, r.saving) : '');
        const ribbon = card.querySelector('[data-pack-ribbon]');
        if (ribbon) ribbon.hidden = ribbon.hasAttribute('data-best') && !(r.q > 1 && r.perUnit === lowUnit);
      });

      const cur = cards.find((c) => c.input === checked);
      const r = cur && cur.r;
      const title = checked.dataset.title;
      const total = r ? money(r.total) : '';
      if (r && !total) console.error('pack-selector: falta el importe', r.total);
      // Fila del precio de arriba con «Seguir al pack» (#price-<sección>[data-pack-price], snippet ficha-precio; 21-10-2026): lo mismo que la tarjeta
      // elegida, con su r y sus cadenas. product-info.js la repinta con el HTML del servidor (pack preseleccionado) al cambiar de variante y avisa aquí
      // en la misma tarea: se corrige antes de pintarse. Es la única región viva de la fila: solo se escribe lo que cambia (cada cambio se lee una vez).
      // 22-10-2026 (noche, ADR-0021): «~~tachado~~ total» y la insignia, como la tarjeta; el tachado, oculto a los lectores de pantalla.
      const row = document.getElementById(`price-${this.section}`);
      if (r && total && row && row.hasAttribute('data-pack-price')) {
        const put = (sel, text) => {
          const el = row.querySelector(sel);
          if (!el) return;
          if (el.textContent !== text) el.textContent = text;
          if (el.hidden !== !text) el.hidden = !text;
        };
        put('[data-pack-price-total]', total);
        put('[data-pack-price-strike]', r.ref !== null ? money(r.ref) : '');
        put('[data-pack-price-badge]', r.saving > 0 ? fill(data.saveText, r.saving) : '');
      }
      document.querySelectorAll(`[data-pack-buy="${this.section}"]`).forEach((btn) => {
        if (!r || !r.available || !total) {
          btn.removeAttribute('href');
          btn.setAttribute('aria-disabled', 'true');
          btn.textContent = btn.dataset.soldOut || '';
          return;
        }
        btn.removeAttribute('aria-disabled');
        const choosing = btn.closest('sticky-buy[data-choosing]');
        if (choosing) {
          btn.setAttribute('href', '#packs');
          btn.textContent = choosing.dataset.choose;
          return;
        }
        btn.setAttribute('href', `${this.cartBase}/${r.items.map((i) => `${i.id}:${i.quantity}`).join(',')}`);
        btn.textContent = (btn.dataset.template || '')
          .replace('{title}', () => title)
          .replace('{total}', () => total)
          .replace('{qty}', () => String(r.q));
      });
      document.querySelectorAll(`sticky-buy[data-section-id="${this.section}"]`).forEach((bar) => {
        // «Desde» + el menor total de las tarjetas, como con las de siempre
        const from = r && r.available && bar.dataset.from && bar.hasAttribute('data-choosing');
        const cents = from ? Math.min(...shown.map((c) => c.r.total)) : r ? r.total : null;
        if (cents === null || !money(cents)) return;
        bar.querySelector('[data-pack-summary-title]').textContent = from ? bar.dataset.from : title;
        bar.querySelector('[data-pack-summary-total]').textContent = money(cents);
      });
      if (r) {
        this.publish({
          items: r.items,
          units: r.q,
          total_cents: r.total,
          per_unit_cents: r.perUnit,
          compare_cents: r.ref,
          saving_cents: r.saving,
          available: r.available,
        });
      }
    }
  }

  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const main = document.querySelector(`[data-pack-buy-main][data-pack-buy="${this.dataset.sectionId}"]`);
      if (!main || !('IntersectionObserver' in window)) return;
      // Siempre hay un botón de compra a la vista, y solo uno: la barra se enseña cuando no se ve ni el botón principal ni el de la
      // banda de cierre (también al entrar en la página) y se retira en cuanto se ve cualquiera de los dos. «A la vista» = por debajo
      // de la cabecera fija. El primer aviso del observador trae el estado de todos los botones.
      // Con las tarjetas «precio por unidad» (#packs[data-cards-hide-bar]; ADR-0017, 20-10-2026), también se retira mientras se ve
      // alguna tarjeta: la barra ya no tapa la de abajo, y el botón principal está justo debajo de ellas.
      const header = document.querySelector('.lp-header');
      const top = header ? Math.round(header.getBoundingClientRect().height) : 0;
      const packs = document.getElementById('packs');
      const cards = packs && packs.hasAttribute('data-cards-hide-bar') ? packs.querySelector('.pack-selector__cards') : null;
      // 27-10-2026: en ordenador (desde 990 px) la tarjeta de compra queda fija arriba mientras pasa la galería y su botón principal puede quedar
      // bajo el borde con las tarjetas de pack a la vista: allí las tarjetas NO retiran la barra (solo la retiran el botón principal, el de la
      // banda de cierre, el cierre con escena y el pie) y la barra compra directo (sin «Elegir pack»). En móvil y tableta, todo como antes.
      const desk = window.matchMedia('(min-width: 990px)');
      const seen = new Set();
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? seen.add(entry.target) : seen.delete(entry.target)));
          const show = seen.size === 0;
          this.hidden = !show;
          document.body.classList.toggle('has-sticky-buy', show);
        },
        { rootMargin: `-${top}px 0px 0px 0px` }
      );
      // 26-10-2026: la barra tampoco tapa el cierre con escena ni el pie: se retira mientras se ve cualquiera de los dos (y vuelve al subir)
      [main, ...document.querySelectorAll('[data-lp-closing-cta], .lp-closing--scene, .lp-footer')].forEach((el) => this.observer.observe(el));
      // Las tarjetas solo se vigilan por debajo de 990 px; al cruzar el ancho (girar la pantalla, cambiar el tamaño de la ventana) se ajusta
      const watchCards = () => {
        if (!cards) return;
        if (!desk.matches) this.observer.observe(cards);
        else {
          this.observer.unobserve(cards);
          if (seen.delete(cards)) {
            const show = seen.size === 0;
            this.hidden = !show;
            document.body.classList.toggle('has-sticky-buy', show);
          }
        }
      };
      watchCards();
      let sync = () => {};
      this.onDesk = () => {
        watchCards();
        sync();
      };
      desk.addEventListener('change', this.onDesk);
      this.desk = desk;

      // «Elegir pack» hasta que el cliente ve el selector; después, «Comprar». Mientras no lo ha visto (data-choosing), el botón de la
      // barra dice el texto de data-choose y baja a #packs: lo pinta PackSelector.render(), que es quien escribe los botones (agotado
      // manda). «Visto» = la mitad o más del alto de #packs ha estado entre la cabecera fija y la barra; desde entonces el botón compra
      // el pack elegido y no vuelve atrás. Con el texto vacío, o si el selector no está en marcha, la barra compra directo desde el
      // principio (el HTML servido ya trae el enlace de compra).
      // En ordenador (desde 990 px) no hay «Elegir pack»: las tarjetas ya están a la vista y la barra compra el pack elegido desde el principio.
      if (!this.dataset.choose || !packs || !packs.data) return;
      let packsSeen = false;
      sync = () => {
        const want = !packsSeen && !desk.matches;
        if (want === this.hasAttribute('data-choosing')) return;
        if (want) this.setAttribute('data-choosing', '');
        else this.removeAttribute('data-choosing');
        packs.render();
      };
      sync();
      const bar = parseFloat(getComputedStyle(this).minHeight) || 0;
      this.packsObserver = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.intersectionRatio >= 0.5)) return;
          this.packsObserver.disconnect();
          packsSeen = true;
          sync();
        },
        { rootMargin: `-${top}px 0px -${Math.round(bar)}px 0px`, threshold: 0.5 }
      );
      this.packsObserver.observe(packs);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.packsObserver) this.packsObserver.disconnect();
      if (this.desk && this.onDesk) this.desk.removeEventListener('change', this.onDesk);
    }
  }

  // Al pulsar compra, respuesta inmediata mientras se abre el pago.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-pack-buy-main]');
    if (!btn || !btn.dataset.loading || btn.getAttribute('aria-disabled') === 'true') return;
    btn.textContent = btn.dataset.loading;
    btn.classList.add('is-loading');
  });

  // Si el navegador restaura la página al volver atrás, se repone el texto del botón.
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('.is-loading').forEach((btn) => btn.classList.remove('is-loading'));
    const selector = document.querySelector('pack-selector');
    if (selector) selector.render();
  });

  customElements.define('pack-selector', PackSelector);
  customElements.define('sticky-buy', StickyBuy);
})();
